import { useContext, useEffect, useRef, useState } from 'react';
import { ApiContext, errorText } from '../../../lib/api';
import { positionAt } from '../../../lib/s3/plan';
import {
  isNotAvailable,
  previewOverwatch,
  type OverwatchPreviewResponse,
} from '../../../lib/s3Api';
import type { OverwatchDef, OverwatchPerchDef } from '../../../lib/s3/types';
import { inputCls, smallBtn } from '../fields';
import { PlanView } from './PlanView';

/**
 * The plan preview (sniper.html "Admin"): pick a mission and a seed; the API runs OverwatchSim over the DRAFT section
 * (`POST /admin/overwatch/preview`, S3-OWS) and returns sampled positions, which play here on the scene plan with a clock, play/pause
 * and a scrubber, so a designer can check routes and timing without a phone. The phase bands and the rolled hidden truths are listed.
 */
export function OverwatchPlanPreview({ overwatch }: { overwatch: OverwatchDef }) {
  const api = useContext(ApiContext);
  const missions = (overwatch.missions ?? []).filter((m) => !!m);
  const [missionId, setMissionId] = useState(missions[0]?.id ?? '');
  const [seed, setSeed] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<OverwatchPreviewResponse | null>(null);
  const [t, setT] = useState(0);
  const [playing, setPlaying] = useState(false);
  const last = useRef<number | null>(null);
  const mission = missions.find((m) => m?.id === missionId) ?? missions[0];
  const scene = (overwatch.scenes ?? []).find((s) => s?.id === mission?.sceneId);
  const duration = (result?.endMs ?? 0) / 1000;

  useEffect(() => {
    if (!playing) return;
    let frame = 0;
    const tick = (now: number) => {
      const dt = last.current == null ? 0 : (now - last.current) / 1000;
      last.current = now;
      setT((x) => {
        const next = x + dt;
        if (next >= duration) {
          setPlaying(false);
          return duration;
        }
        return next;
      });
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frame);
      last.current = null;
    };
  }, [playing, duration]);

  const run = async () => {
    if (!api || !mission?.id) return;
    setBusy(true);
    setError(null);
    setPlaying(false);
    try {
      const r = await previewOverwatch(api.client, {
        overwatch,
        missionId: mission.id,
        seed,
        stepMs: 200,
      });
      setResult(r);
      setT(0);
    } catch (e) {
      setResult(null);
      const text = errorText(e);
      setError(
        isNotAvailable(text) ? `Not available yet: the plan preview is S3-OWS's. (${text})` : text,
      );
    } finally {
      setBusy(false);
    }
  };

  const ms = t * 1000;
  const phase = result?.phases?.find((p) => ms >= (p.startMs ?? 0) && ms < (p.endMs ?? 0));
  const figures = (result?.figures ?? []).map((f) => ({
    id: f.id,
    label: `${f.id} ${f.figureId}${f.traits && Object.keys(f.traits).length ? ` (${Object.values(f.traits).join(', ')})` : ''}${f.tells?.length ? ` · tells ${f.tells.join(', ')}` : ''}`,
    role: f.role,
    at: f.diedMs > 0 && ms > f.diedMs ? null : positionAt(f.samples ?? [], ms),
  }));
  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center gap-2 text-xs">
        <label className="flex items-center gap-1">
          mission
          <select
            aria-label="Preview mission"
            className={`${inputCls} w-auto`}
            value={mission?.id ?? ''}
            onChange={(e) => setMissionId(e.target.value)}
          >
            {missions.length === 0 && <option value="">(no missions)</option>}
            {missions.map((m) => (
              <option key={m!.id} value={m!.id}>
                {m!.name || m!.id}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-1">
          seed
          <input
            aria-label="Preview seed"
            type="number"
            className={`${inputCls} w-24`}
            value={seed}
            onChange={(e) => setSeed(Math.trunc(Number(e.target.value)) || 0)}
          />
        </label>
        <button
          type="button"
          className={smallBtn}
          disabled={!api || busy || !mission}
          onClick={() => void run()}
        >
          {busy ? 'Simulating…' : 'Simulate on the server'}
        </button>
        {result && (
          <>
            <button
              type="button"
              className={smallBtn}
              onClick={() => (t >= duration ? (setT(0), setPlaying(true)) : setPlaying(!playing))}
            >
              {playing ? 'Pause' : 'Play'}
            </button>
            <input
              aria-label="Preview time"
              type="range"
              min={0}
              max={duration}
              step={0.1}
              value={t}
              onChange={(e) => {
                setPlaying(false);
                setT(Number(e.target.value));
              }}
            />
            <span className="font-mono" role="timer">
              {t.toFixed(1)} / {duration.toFixed(1)} s{phase ? ` · ${phase.id}` : ''}
            </span>
          </>
        )}
      </div>
      {error && <p className="mb-2 text-xs text-blam">{error}</p>}
      {scene ? (
        <PlanView
          plan={scene.plan}
          perches={(scene.perches ?? []) as OverwatchPerchDef[]}
          figures={figures}
          label={`Plan preview of ${mission?.id ?? ''}`}
        />
      ) : (
        <p className="text-sm text-bruise">Pick a mission whose scene is in the draft.</p>
      )}
      {result && (
        <div className="mt-2 grid gap-2 text-xs sm:grid-cols-2">
          {Object.entries(result.descriptions ?? {}).map(([ph, text]) => (
            <p key={ph} className="sm:col-span-2">
              Description ({ph}): {text}
            </p>
          ))}
          <div>
            <p className="font-semibold">Phases</p>
            <ul>
              {(result.phases ?? []).map((p) => (
                <li key={p.id}>
                  {p.id}: {((p.startMs ?? 0) / 1000).toFixed(1)} s → {((p.endMs ?? 0) / 1000).toFixed(1)} s ·{' '}
                  {p.won ? 'won' : 'not won'} (no shots)
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="font-semibold">Hidden (rolled)</p>
            <ul className="font-mono">
              {Object.entries(result.block?.hidden ?? {}).map(([k, v]) => (
                <li key={k}>
                  {k} = {String(v)}
                </li>
              ))}
            </ul>
          </div>
          {result.errors?.length ? (
            <ul className="text-blam sm:col-span-2">
              {result.errors.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          ) : null}
          <p className="sm:col-span-2 text-bruise">
            {result.figures.length} figures · colours: hostile red, escort cyan, innocent yellow.
          </p>
        </div>
      )}
    </div>
  );
}
